// Perfil del huésped (CRM) y tareas entre departamentos (script 938).
// Componentes reutilizados por el detalle de la reserva, la nueva reserva y el
// dashboard: avisos (VIP, alergias, lista negra), ficha con historial, editor
// del perfil, recados/tareas de la reserva y pendientes del día.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Crown, ListTodo, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Campo, Panel, Switch, fieldCls, money, fmtFecha, hotelRpc, QK, ESTADO_RESERVA, type EstadoReserva } from './shared';

export interface PerfilHuesped { vip: boolean; preferencias: string | null; alergias: string | null; notas: string | null; lista_negra: boolean; motivo_lista: string | null; estadias?: number }
export interface Traza { id: string; tipo: 'traza' | 'recado'; departamento: string; texto: string; fecha: string; resuelta: boolean }
interface Pendiente extends Traza { vencida: boolean; habitacion: string | null; reserva: string | null; reserva_id: string | null; huesped: string | null }
interface Ficha {
  cliente: { id: string; nombre: string; identificacion: string | null; email: string | null; telefono: string | null; nacionalidad: string | null } | null;
  perfil: Partial<PerfilHuesped>;
  resumen: { estadias: number; noches: number; no_shows: number; cancelaciones: number; ultima_visita: string | null; gasto: number };
  reservas: { id: string; codigo: string; estado: EstadoReserva; llegada: string; salida: string; tipo: string; habitacion: string | null; total: number }[];
  objetos: { codigo: string; descripcion: string; estado: string }[];
}
export const DEPTOS: Record<string, string> = { recepcion: 'Recepción', housekeeping: 'Housekeeping', mantenimiento: 'Mantenimiento', ayb: 'A&B', gerencia: 'Gerencia' };

/** Avisos compactos del perfil (para cabeceras). */
export function AvisosPerfil({ perfil }: { perfil?: Partial<PerfilHuesped> | null }) {
  if (!perfil) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
      {perfil.lista_negra && <span className="inline-flex items-center gap-1 rounded bg-red-600 px-2 py-0.5 font-semibold text-white"><Ban className="h-3 w-3" /> Lista negra{perfil.motivo_lista ? `: ${perfil.motivo_lista}` : ''}</span>}
      {perfil.vip && <span className="inline-flex items-center gap-1 rounded bg-amber-400 px-2 py-0.5 font-semibold text-amber-950"><Crown className="h-3 w-3" /> VIP</span>}
      {perfil.alergias && <span className="inline-flex items-center gap-1 rounded bg-rose-100 px-2 py-0.5 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300"><AlertTriangle className="h-3 w-3" /> Alergias: {perfil.alergias}</span>}
      {perfil.preferencias && <span className="rounded bg-sky-100 px-2 py-0.5 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300">Prefiere: {perfil.preferencias}</span>}
      {Number(perfil.estadias ?? 0) > 0 && <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">{perfil.estadias} estadía(s) anteriores</span>}
    </div>
  );
}

/** Ficha 360 del cliente con edición del perfil. */
export function ModalFichaHuesped({ partnerId, onClose }: { partnerId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasAccessLevel } = usePermissions();
  const { data: f } = useQuery<Ficha>({ queryKey: [QK, 'ficha', partnerId], queryFn: () => hotelRpc<Ficha>('huesped_ficha', { _partner: partnerId }) });
  const [ed, setEd] = useState<PerfilHuesped | null>(null);
  const p: PerfilHuesped = ed ?? { vip: !!f?.perfil.vip, preferencias: f?.perfil.preferencias ?? '', alergias: f?.perfil.alergias ?? '', notas: f?.perfil.notas ?? '',
    lista_negra: !!f?.perfil.lista_negra, motivo_lista: f?.perfil.motivo_lista ?? '' };
  const set = (x: Partial<PerfilHuesped>) => setEd({ ...p, ...x });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('huesped_perfil_guardar', { _partner: partnerId, _d: p }),
    onSuccess: () => { toast.success('Perfil guardado'); setEd(null); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (!f) return <Panel title="Ficha del huésped" onClose={onClose}><p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p></Panel>;
  return (
    <Panel title={`Ficha · ${f.cliente?.nombre ?? ''}`} onClose={onClose} wide>
      <div className="space-y-3 text-sm">
        <div className="text-xs text-muted-foreground">
          {f.cliente?.identificacion} {f.cliente?.email && `· ${f.cliente.email}`} {f.cliente?.telefono && `· ${f.cliente.telefono}`} {f.cliente?.nacionalidad && `· ${f.cliente.nacionalidad}`}
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
          {[['Estadías', f.resumen.estadias], ['Noches', f.resumen.noches], ['Gasto total', money(Number(f.resumen.gasto))],
            ['Última visita', fmtFecha(f.resumen.ultima_visita)], ['No-shows', f.resumen.no_shows], ['Cancelaciones', f.resumen.cancelaciones]].map(([l, v]) => (
            <div key={String(l)} className="rounded border p-2"><div className="text-[11px] text-muted-foreground">{l}</div><div className="font-semibold">{v}</div></div>
          ))}
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          <Campo label="Preferencias"><input className={fieldCls} value={p.preferencias ?? ''} onChange={(e) => set({ preferencias: e.target.value })} placeholder="Piso alto, almohada firme, cama king…" /></Campo>
          <Campo label="Alergias / restricciones"><input className={fieldCls} value={p.alergias ?? ''} onChange={(e) => set({ alergias: e.target.value })} placeholder="Mariscos, gluten…" /></Campo>
          <Campo label="Notas internas" className="md:col-span-2"><input className={fieldCls} value={p.notas ?? ''} onChange={(e) => set({ notas: e.target.value })} /></Campo>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Switch checked={p.vip} onChange={(v) => set({ vip: v })} label="Huésped VIP" />
          <Switch checked={p.lista_negra} disabled={!hasAccessLevel(4)} onChange={(v) => set({ lista_negra: v })} label="Lista negra (supervisor)" />
        </div>
        {p.lista_negra && <Campo label="Motivo de la lista negra *"><input className={fieldCls} value={p.motivo_lista ?? ''} onChange={(e) => set({ motivo_lista: e.target.value })} /></Campo>}
        <div className="flex justify-end"><Button size="sm" isLoading={guardar.isPending} disabled={!ed} onClick={() => guardar.mutate()}>Guardar perfil</Button></div>
        <div className="max-h-48 overflow-y-auto rounded border">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-1 pl-2 pr-2">Reserva</th><th className="py-1 pr-2">Fechas</th><th className="py-1 pr-2">Habitación</th><th className="py-1 pr-2">Estado</th><th className="py-1 pr-2 text-right">Total</th></tr></thead>
            <tbody>
              {f.reservas.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-1 pl-2 pr-2 font-mono">{r.codigo}</td><td className="py-1 pr-2">{fmtFecha(r.llegada)} → {fmtFecha(r.salida)}</td>
                  <td className="py-1 pr-2">{r.tipo}{r.habitacion ? ` · ${r.habitacion}` : ''}</td>
                  <td className="py-1 pr-2">{ESTADO_RESERVA[r.estado]?.label ?? r.estado}</td><td className="py-1 pr-2 text-right tabular-nums">{money(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {f.objetos.length > 0 && <p className="text-xs text-amber-700 dark:text-amber-400">Tiene objetos olvidados en custodia: {f.objetos.map((o) => `${o.codigo} ${o.descripcion}`).join(' · ')}</p>}
      </div>
    </Panel>
  );
}

/** Recados y tareas de una reserva (detalle). */
export function TrazasReserva({ reservaId, trazas }: { reservaId: string; trazas: Traza[] }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ tipo: 'traza', departamento: 'housekeeping', texto: '', fecha: '' });
  const crear = useMutation({
    mutationFn: () => hotelRpc('traza_crear', { _d: { reserva_id: reservaId, ...f, fecha: f.fecha || null } }),
    onSuccess: () => { toast.success('Registrado'); setF({ ...f, texto: '' }); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const resolver = useMutation({
    mutationFn: (id: string) => hotelRpc('traza_resolver', { _id: id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [QK] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="space-y-2 rounded-md border p-2 text-xs">
      <div className="flex items-center gap-1 font-medium"><MessageSquare className="h-3.5 w-3.5" /> Recados y tareas</div>
      {trazas.map((t) => (
        <div key={t.id} className={`flex items-start justify-between gap-2 ${t.resuelta ? 'text-muted-foreground line-through' : ''}`}>
          <span><b>{t.tipo === 'recado' ? 'Recado' : DEPTOS[t.departamento] ?? t.departamento}</b> · {fmtFecha(t.fecha)} · {t.texto}</span>
          {!t.resuelta && <button type="button" className="shrink-0 text-primary underline" onClick={() => resolver.mutate(t.id)}>Hecho</button>}
        </div>
      ))}
      <div className="grid grid-cols-2 gap-1 md:grid-cols-6">
        <select className={`${fieldCls} h-8 text-xs`} value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>
          <option value="traza">Tarea</option><option value="recado">Recado al huésped</option>
        </select>
        <select className={`${fieldCls} h-8 text-xs`} value={f.departamento} disabled={f.tipo === 'recado'} onChange={(e) => setF({ ...f, departamento: e.target.value })}>
          {Object.entries(DEPTOS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input type="date" className={`${fieldCls} h-8 text-xs`} value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} title="Para cuándo (vacío = hoy)" />
        <input className={`${fieldCls} col-span-2 h-8 text-xs`} value={f.texto} placeholder="Ej.: cuna en la habitación, llamar a las 7:00…" onChange={(e) => setF({ ...f, texto: e.target.value })} />
        <Button size="sm" className="h-8" isLoading={crear.isPending} disabled={!f.texto.trim()} onClick={() => crear.mutate()}>Agregar</Button>
      </div>
    </div>
  );
}

/** Tareas y recados pendientes del día (dashboard). */
export function TareasPendientes() {
  const qc = useQueryClient();
  const [depto, setDepto] = useState('');
  const { data = [] } = useQuery<Pendiente[]>({ queryKey: [QK, 'trazas', depto], queryFn: () => hotelRpc<Pendiente[]>('trazas_pendientes', { _departamento: depto || null }) });
  const resolver = useMutation({
    mutationFn: (id: string) => hotelRpc('traza_resolver', { _id: id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [QK] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><ListTodo className="h-4 w-4" /> Tareas y recados de hoy ({data.length})</CardTitle>
        <select className={`${fieldCls} h-8 w-40 text-xs`} value={depto} onChange={(e) => setDepto(e.target.value)}>
          <option value="">Todos</option>{Object.entries(DEPTOS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {data.length === 0 && <p className="text-xs text-muted-foreground">Nada pendiente ✓</p>}
        {data.map((t) => (
          <div key={t.id} className="flex items-start justify-between gap-2 border-b pb-1 last:border-0">
            <span className={t.vencida ? 'text-red-700 dark:text-red-400' : ''}>
              <b>{t.tipo === 'recado' ? 'Recado' : DEPTOS[t.departamento]}</b>{t.habitacion ? ` · hab. ${t.habitacion}` : ''}{t.huesped ? ` · ${t.huesped}` : ''}: {t.texto}
              {t.vencida && <span className="ml-1 text-xs">(vencida {fmtFecha(t.fecha)})</span>}
            </span>
            <Button size="sm" variant="outline" className="h-7 shrink-0 px-2 text-xs" onClick={() => resolver.mutate(t.id)}>Hecho</Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
